import { useState } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { NotificationAudience } from '@wundo/shared/notificationHistory.ts';
import type { UserOption } from '@/components/pickers/userOption.ts';
import {
  getNotificationRecipient,
  listNotificationRecipients,
  type NotificationRecipient,
  type NotificationRecipientScope,
} from '@/lib/notificationSend.ts';
import type { CohortWithOrganisation } from '@/types/cohorts.ts';
import type { Organisation } from '@/types/organisations.ts';
import type { NotificationKindChoice } from './useNotificationKind.ts';
import type { SendOptions } from './useSendOptions.ts';

export type ScopeKind = 'user' | 'organisation' | 'cohort';

/** How staff look for the people: one scope at a time, with what was picked in it. */
export type RecipientSearch =
  | { scope: 'user'; user: UserOption | null }
  | { scope: 'organisation'; organisation: Organisation | null }
  | { scope: 'cohort'; cohort: CohortWithOrganisation | null };

export interface Recipients {
  search: RecipientSearch;
  /** The person the user picker handed back, read in full. */
  picked: UseQueryResult<NotificationRecipient>;
  candidates: UseQueryResult<NotificationRecipient[]>;
  /** The one person a meeting moment goes to; a user moment goes to a list instead. */
  recipient: NotificationRecipient | null;
  /** The people on screen: the list a scope named, or the one name picked. */
  people: NotificationRecipient[];
  audience: NotificationAudience | undefined;
  /** Whose roleplay library to pick from: the organisation the people are on. */
  libraryOrganisationId: string | null;
  changeScope: (scope: ScopeKind) => void;
  pickUser: (user: UserOption | null) => void;
  changeOrganisation: (organisation: Organisation | null) => void;
  changeCohort: (cohort: CohortWithOrganisation | null) => void;
  pickFromList: (person: NotificationRecipient) => void;
  clearPicks: () => void;
}

interface RecipientsInput {
  /** Null when the header says "All organisations". */
  headerOrganisation: Organisation | null;
  kind: NotificationKindChoice;
  options: SendOptions;
}

/* With one organisation in the header, that is the organisation searched. */
function searchFor(
  scope: ScopeKind,
  headerOrganisation: Organisation | null,
): RecipientSearch {
  switch (scope) {
    case 'user':
      return { scope, user: null };
    case 'organisation':
      return { scope, organisation: headerOrganisation };
    case 'cohort':
      return { scope, cohort: null };
  }
}

function askedScopeOf(search: RecipientSearch): NotificationRecipientScope | null {
  if (search.scope === 'organisation' && search.organisation) {
    return { kind: 'organisation', organisationId: search.organisation.id };
  }
  if (search.scope === 'cohort' && search.cohort) {
    return { kind: 'cohort', cohortId: search.cohort.id };
  }
  return null;
}

/* Who the press is aimed at, so the history can say "To: Cohort …" afterwards. */
function audienceOf(search: RecipientSearch): NotificationAudience | undefined {
  if (search.scope === 'organisation') {
    return search.organisation
      ? { kind: 'organisation', id: search.organisation.id }
      : undefined;
  }
  if (search.scope === 'cohort') {
    return search.cohort ? { kind: 'cohort', id: search.cohort.id } : undefined;
  }
  return search.user ? { kind: 'user', id: search.user.id } : undefined;
}

function libraryOrganisationOf(
  search: RecipientSearch,
  picked: NotificationRecipient | undefined,
): string | null {
  if (search.scope === 'organisation') return search.organisation?.id ?? null;
  if (search.scope === 'cohort') return search.cohort?.organisation.id ?? null;
  return picked?.organisation_id ?? null;
}

export function useRecipients({
  headerOrganisation,
  kind,
  options,
}: RecipientsInput): Recipients {
  const [search, setSearch] = useState<RecipientSearch>(
    searchFor('user', headerOrganisation),
  );
  const [pickedFromList, setPickedFromList] = useState<NotificationRecipient | null>(
    null,
  );

  const pickedUser = search.scope === 'user' ? search.user : null;
  const askedScope = askedScopeOf(search);

  /* The picker only carries an id; the record behind it is read where every screen reads a user. */
  const picked = useQuery({
    queryKey: ['notificationRecipient', pickedUser?.id],
    queryFn: () => getNotificationRecipient(pickedUser!.id),
    enabled: pickedUser !== null,
  });

  /* A user moment has no single recipient: one name picked by hand is a list of one. */
  const recipient = kind.isUserScope ? null : (pickedFromList ?? picked.data ?? null);

  /* Only a list for a user moment says who a send would leave out, so only it takes the channel. */
  const candidates = useQuery({
    queryKey: [
      'notificationRecipients',
      askedScope,
      kind.isUserScope ? kind.kind : null,
      options.roleplayId,
      kind.isUserScope ? options.channel : undefined,
      kind.isUserScope && options.resend,
    ],
    queryFn: () =>
      listNotificationRecipients({
        scope: askedScope!,
        kind: kind.isUserScope ? kind.kind : undefined,
        roleplayId: options.roleplayId,
        channel: kind.isUserScope ? options.channel : undefined,
        resend: kind.isUserScope && options.resend,
      }),
    enabled: askedScope !== null && recipient === null,
  });

  const showsPickedUser = kind.isUserScope && search.scope === 'user';
  const pickedPeople = picked.data ? [picked.data] : [];

  const changeScope = (scope: ScopeKind) => {
    setSearch(searchFor(scope, headerOrganisation));
    setPickedFromList(null);
  };

  const changeOrganisation = (organisation: Organisation | null) => {
    setSearch({ scope: 'organisation', organisation });
    setPickedFromList(null);
  };

  const changeCohort = (cohort: CohortWithOrganisation | null) => {
    setSearch({ scope: 'cohort', cohort });
    setPickedFromList(null);
  };

  const clearPicks = () => {
    setPickedFromList(null);
    setSearch((current) =>
      current.scope === 'user' ? { scope: 'user', user: null } : current,
    );
  };

  return {
    search,
    picked,
    candidates,
    recipient,
    people: showsPickedUser ? pickedPeople : (candidates.data ?? []),
    audience: audienceOf(search),
    libraryOrganisationId: libraryOrganisationOf(search, picked.data),
    changeScope,
    pickUser: (user) => setSearch({ scope: 'user', user }),
    changeOrganisation,
    changeCohort,
    pickFromList: setPickedFromList,
    clearPicks,
  };
}
